import { RACSearchDropdown } from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ClassificationsInputPlaceholder,
  emptyField,
  PoFormField,
} from '../../constants/constants';
import { ClassificationType } from '../../domain/PurchaseOrder/ClassificationType';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { SelectedFieldOption } from '../../types/types';
import { filterById, populateSelectByChild } from '../../utils/utils';
import {
  useClassificationsDispatch,
  useClassificationsState,
} from './context/Provider';
import { getClassificationFieldName } from './context/fieldNames';
import { ClassificationsComponentProps } from './context/types';

export const subdepartmentTestId = 'subdepartmentComponent';

// eslint-disable-next-line
export const SubDepartment = (props: ClassificationsComponentProps) => {
  const { name, label, purchaseOrder, qrScannerOutput, showErrorMessage } =
    props;
  const { errors, setValue } = useFormContext();
  const { startWithDepartment, subDepartments } = useClassificationsState();
  const dispatch = useClassificationsDispatch();
  const [itemValue, setItemValue] = useState(emptyField);
  const [isViewMode, setIsViewMode] = useState(false);
  const getFormFieldName = useCallback(
    (fieldName: PoFormField) => getClassificationFieldName(name, fieldName),
    [name]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    const poSubDepartment = purchaseOrder && purchaseOrder.subDepartment;
    const selectedSubDepartment: {
      name: string;
      id: string;
      brackets: [];
    } | null =
      subDepartments &&
      poSubDepartment &&
      filterById(subDepartments, poSubDepartment)[0];

    if (subDepartments && subDepartments.length === 1) {
      setItemValue({
        value: subDepartments[0].id,
        label: subDepartments[0].name,
      });

      dispatch({
        type: ClassificationType.SET_SINGLE_BRACKET,
        payload: subDepartments[0].brackets,
      });
    } else {
      setItemValue({
        value: (selectedSubDepartment && selectedSubDepartment.id) || '',
        label:
          (selectedSubDepartment && selectedSubDepartment.name) ||
          ClassificationsInputPlaceholder,
      });

      selectedSubDepartment && selectedSubDepartment.brackets
        ? dispatch({
            type: ClassificationType.SET_SUBDEPARTMENTS,
            payload: selectedSubDepartment.brackets,
          })
        : dispatch({
            type: ClassificationType.CLEAR_BRACKETS,
            payload: [],
          });
    }

    if (qrScannerOutput) {
      const selectedItem = subDepartments?.find(
        (o) => o.id == qrScannerOutput.subDepartmentNumber
      );
      setItemValue({
        value: selectedItem?.id
          ? selectedItem?.id
          : qrScannerOutput?.subDepartmentNumber?.toString() || '',
        label: selectedItem?.name
          ? selectedItem?.name
          : qrScannerOutput?.subDepartment
          ? qrScannerOutput?.subDepartment
          : '',
      });
      setValue(
        getFormFieldName(PoFormField.SubDepartment),
        selectedItem?.id
          ? selectedItem?.id
          : qrScannerOutput?.subDepartmentNumber?.toString(),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setIsViewMode(
        qrScannerOutput?.isScannedCategoryKept
          ? qrScannerOutput?.isScannedCategoryKept
          : false
      );
      if (!qrScannerOutput.departmentNumber) {
        setItemValue(emptyField);
        dispatch({
          type: ClassificationType.CLEAR_BRACKETS,
        });
      }
      // }
    }
    // eslint-disable-next-line
  }, [subDepartments, dispatch, qrScannerOutput]);

  // Flag if dashboard-sent value is not found in classification options
  useEffect(() => {
    if (qrScannerOutput || !subDepartments?.length) return;
    const prefillValue = purchaseOrder?.subDepartment;
    if (!prefillValue) return;
    const resolved = subDepartments.some((sd: any) => sd.id == prefillValue);
    setTimeout(
      () => setValue(name + '__unresolved', resolved ? '' : 'true'),
      0
    );
    // eslint-disable-next-line
  }, [subDepartments, qrScannerOutput]);

  const handleChange = useCallback(
    // eslint-disable-next-line
    (selected: SelectedFieldOption) => {
      const { value, label, brackets } = selected;
      setItemValue({ value, label });
      setValue(name, value, { shouldValidate: true, shouldDirty: true });

      if (purchaseOrder && purchaseOrder.subDepartment !== value) {
        purchaseOrder.bracket = '';
        if (startWithDepartment) {
          purchaseOrder.itemType = '';
          purchaseOrder.itemSubType = '';
        }
      }

      if (brackets) {
        dispatch({
          type: ClassificationType.SET_SUBDEPARTMENTS,
          payload: brackets,
        });
        if (brackets.length === 1) {
          setValue(getFormFieldName(PoFormField.Bracket), brackets[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          setValue(getFormFieldName(PoFormField.Bracket), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
        if (startWithDepartment) {
          const filterdTypes = brackets[0].types;
          const filterdSubTypes = filterdTypes[0].subTypes;
          if (filterdTypes.length === 1) {
            setValue(
              getFormFieldName(PoFormField.ItemType),
              filterdTypes[0].id,
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
          } else {
            setValue(getFormFieldName(PoFormField.ItemType), '', {
              shouldValidate: false,
              shouldDirty: true,
            });
          }

          if (filterdSubTypes.length === 1) {
            setValue(
              getFormFieldName(PoFormField.ItemSubType),
              filterdSubTypes[0].id,
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
          } else {
            setValue(getFormFieldName(PoFormField.ItemSubType), '', {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
        }
      }
    },
    [
      dispatch,
      getFormFieldName,
      name,
      purchaseOrder,
      setValue,
      startWithDepartment,
    ]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      setIsViewMode(
        purchaseOrder
          ? purchaseOrder?.poStatus === PoStatus.SUBMITTED ||
              purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
          : false
      );
    },
    [purchaseOrder]
  );

  return useMemo(
    () => (
      <Controller
        name={name}
        rules={{ required: true }}
        render={({ name }) => (
          <RACSearchDropdown
            name={name}
            inputLabel={label}
            options={populateSelectByChild(subDepartments, 'brackets') || []}
            OnChange={handleChange}
            required
            disabled={
              isViewMode || (subDepartments && subDepartments.length === 0)
            }
            value={itemValue}
            errormessage={
              showErrorMessage && get(errors, name)
                ? `${label} is required`
                : ''
            }
          />
        )}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isViewMode,
      purchaseOrder,
      subDepartments,
      handleChange,
      itemValue,
      errors,
      label,
      name,
      showErrorMessage,
    ]
  );
};
